import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import {
  getVendorWhatsappTemplates,
  updateVendorWhatsappTemplate,
  submitVendorWhatsappTemplate,
  syncVendorWhatsappTemplateStatus,
  setVendorWhatsappTemplateEnabled
} from '../../services/api/implementation/impl';

function templateStatusBadgeClass(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'PENDING' || s === 'IN_REVIEW') return 'bg-amber-50 text-amber-800 border-amber-200';
  if (s === 'APPROVED' || s === 'ACTIVE')
    return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  if (s === 'REJECTED' || s === 'FAILED' || s === 'DISABLED')
    return 'bg-rose-50 text-rose-800 border-rose-200';
  return 'bg-gray-50 text-gray-700 border-gray-200';
}

function StatusBadge({ status }) {
  if (!status) return null;
  return (
    <span
      className={`inline-flex w-fit shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${templateStatusBadgeClass(
        status
      )}`}>
      {String(status).replace(/_/g, ' ')}
    </span>
  );
}

function previewBody(bodyText, placeholders = []) {
  let text = String(bodyText || '');
  placeholders.forEach((ph) => {
    if (!ph?.token) return;
    text = text.split(ph.token).join(ph.example || ph.label || ph.token);
  });
  return text;
}

function missingRequiredPlaceholders(bodyText, placeholders = []) {
  const body = String(bodyText || '');
  return placeholders.filter((ph) => ph?.token && !body.includes(ph.token));
}

function EventTemplateCard({
  event,
  draft,
  onChangeDraft,
  onSave,
  onSubmit,
  onToggleEnabled,
  busyKey,
  textareaRefs
}) {
  const status = String(event?.status || 'DRAFT').toUpperCase();
  const locked = status === 'PENDING';
  const approved = status === 'APPROVED';
  const isBusy = busyKey === event.event_key;
  const missing = missingRequiredPlaceholders(draft.body_text, event.placeholders);
  const preview = previewBody(draft.body_text, event.placeholders);
  const templateName = event.pinggo_template_name || event.template_name || '';
  const category = event.category || '';

  const insertToken = (token) => {
    const el = textareaRefs.current[event.event_key];
    const current = draft.body_text || '';
    if (!el) {
      onChangeDraft(event.event_key, { body_text: `${current}${token}` });
      return;
    }
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const next = `${current.slice(0, start)}${token}${current.slice(end)}`;
    onChangeDraft(event.event_key, { body_text: next });
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <section className="space-y-4 rounded-xl border border-stone-200 bg-stone-50/50 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900">{event.title}</h3>
            <StatusBadge status={status} />
          </div>
          <p className="mt-1 text-sm text-gray-500">{event.description}</p>
          {(templateName || category) && (
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
              {templateName ? (
                <span>
                  <span className="font-medium text-gray-500">Template:</span>{' '}
                  <span className="font-mono text-[11px] text-gray-800">{templateName}</span>
                </span>
              ) : null}
              {category ? (
                <span className="inline-flex items-center rounded-full border border-stone-200 bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-700">
                  {String(category).replace(/_/g, ' ')}
                </span>
              ) : null}
            </div>
          )}
          {event.last_error ? (
            <p className="mt-2 text-xs text-rose-700">{event.last_error}</p>
          ) : null}
        </div>
        <label
          className={`flex items-center gap-2 text-sm font-medium ${
            approved ? 'text-gray-800' : 'text-gray-400'
          }`}
          title={approved ? 'Use this template for WhatsApp sends' : 'Enable only after Meta approval'}>
          <input
            type="checkbox"
            checked={!!draft.enabled}
            disabled={!approved || isBusy}
            onChange={(e) => onToggleEnabled(event.event_key, e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-customBlue focus:ring-customBlue"
          />
          Enabled
        </label>
      </div>

      <div>
        <label className="mb-1.5 block text-xs font-semibold text-gray-600">Body</label>
        <textarea
          ref={(node) => {
            textareaRefs.current[event.event_key] = node;
          }}
          value={draft.body_text}
          disabled={locked || isBusy}
          onChange={(e) => onChangeDraft(event.event_key, { body_text: e.target.value })}
          rows={5}
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm focus:border-customBlue focus:outline-none focus:ring-1 focus:ring-customBlue disabled:bg-gray-100"
        />
        <div className="mt-2 flex flex-wrap gap-2">
          {(event.placeholders || []).map((ph) => (
            <button
              key={ph.token}
              type="button"
              disabled={locked || isBusy}
              onClick={() => insertToken(ph.token)}
              className="rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-800 hover:bg-sky-100 disabled:opacity-50"
              title={ph.label}>
              {ph.token} {ph.label}
            </button>
          ))}
        </div>
        {missing.length > 0 ? (
          <p className="mt-2 text-xs text-amber-700">
            Missing placeholders: {missing.map((m) => m.token).join(', ')}
          </p>
        ) : null}
      </div>

      <div>
        <label className="mb-1.5 block text-xs font-semibold text-gray-600">Button label</label>
        <input
          type="text"
          value={draft.button_text}
          disabled={locked || isBusy}
          maxLength={25}
          onChange={(e) => onChangeDraft(event.event_key, { button_text: e.target.value })}
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm focus:border-customBlue focus:outline-none focus:ring-1 focus:ring-customBlue disabled:bg-gray-100"
        />
      </div>

      <div>
        <p className="mb-1.5 text-xs font-semibold text-gray-600">Preview</p>
        <div
          className="overflow-hidden rounded-xl border border-[#d1d7db]"
          style={{
            backgroundColor: '#e5ddd5',
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4cfc7' fill-opacity='0.45'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}>
          <div className="px-3 py-4">
            <div className="relative max-w-[85%]">
              {/* bubble tail */}
              <span
                className="absolute -left-1.5 top-0 h-0 w-0"
                style={{
                  borderTop: '6px solid #fff',
                  borderLeft: '8px solid transparent'
                }}
                aria-hidden
              />
              <div className="overflow-hidden rounded-lg rounded-tl-none bg-white shadow-sm">
                <div className="px-3 pb-1 pt-2">
                  <p className="whitespace-pre-wrap text-[13.5px] leading-[1.4] text-[#111b21]">
                    {preview || '—'}
                  </p>
                  <div className="mt-1 flex justify-end">
                    <span className="select-none text-[10px] leading-none text-[#667781]">
                      {new Date().toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
                <div className="border-t border-[#e9edef]">
                  <div className="flex items-center justify-center gap-1.5 px-3 py-2.5">
                    <svg
                      viewBox="0 0 24 24"
                      className="h-3.5 w-3.5 shrink-0 text-[#00a884]"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                    <span className="text-[13.5px] font-medium text-[#00a884]">
                      {draft.button_text || 'Open'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={locked || isBusy || !String(draft.body_text || '').trim()}
          onClick={() => onSave(event.event_key)}
          className="inline-flex items-center justify-center rounded-lg border border-customBlue px-4 py-2 text-sm font-semibold text-customBlue hover:bg-customBlue/5 disabled:opacity-50">
          {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save draft'}
        </button>
        <Button
          type="button"
          disabled={locked || isBusy || !String(draft.body_text || '').trim()}
          onClick={() => onSubmit(event.event_key)}
          isLoading={isBusy}>
          Submit for approval
        </Button>
      </div>
      {locked ? (
        <p className="text-xs text-amber-700">
          Pending Meta approval — editing is locked until sync updates status.
        </p>
      ) : null}
    </section>
  );
}

const VendorWhatsAppTemplatesModal = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const textareaRefs = useRef({});
  const [drafts, setDrafts] = useState({});
  const [busyKey, setBusyKey] = useState(null);

  const {
    data,
    isLoading,
    isError,
    refetch,
    isFetching
  } = useQuery({
    queryKey: ['vendorWhatsappTemplates'],
    queryFn: getVendorWhatsappTemplates,
    enabled: Boolean(isOpen)
  });

  const events = data?.events || [];
  const whatsappConfigured = !!data?.whatsapp_configured;

  useEffect(() => {
    if (!isOpen || !events.length) return;
    const next = {};
    events.forEach((ev) => {
      next[ev.event_key] = {
        body_text: ev.body_text || '',
        button_text: ev.button_text || '',
        enabled: !!ev.enabled
      };
    });
    setDrafts(next);
  }, [isOpen, data]);

  const isDirty = useMemo(() => {
    if (!events.length) return false;
    return events.some((ev) => {
      const d = drafts[ev.event_key];
      if (!d) return false;
      return (
        d.body_text !== (ev.body_text || '') ||
        d.button_text !== (ev.button_text || '') ||
        !!d.enabled !== !!ev.enabled
      );
    });
  }, [drafts, events]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['vendorWhatsappTemplates'] });

  const saveMutation = useMutation({
    mutationFn: ({ eventKey, payload }) => updateVendorWhatsappTemplate(eventKey, payload),
    onSuccess: () => {
      toast.success('Draft saved');
      invalidate();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Could not save draft');
    },
    onSettled: () => setBusyKey(null)
  });

  const submitMutation = useMutation({
    mutationFn: (eventKey) => submitVendorWhatsappTemplate(eventKey),
    onSuccess: (res) => {
      toast.success(res?.message || 'Submitted for Meta approval');
      invalidate();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Submit failed');
    },
    onSettled: () => setBusyKey(null)
  });

  const syncMutation = useMutation({
    mutationFn: syncVendorWhatsappTemplateStatus,
    onSuccess: (res) => {
      toast.success(
        res?.synced != null ? `Synced (${res.synced} updated)` : 'Status synced'
      );
      invalidate();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Sync failed');
    }
  });

  const enabledMutation = useMutation({
    mutationFn: ({ eventKey, enabled }) =>
      setVendorWhatsappTemplateEnabled(eventKey, enabled),
    onSuccess: () => {
      toast.success('Enabled updated');
      invalidate();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Could not update enabled');
      invalidate();
    },
    onSettled: () => setBusyKey(null)
  });

  const onChangeDraft = (eventKey, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [eventKey]: { ...prev[eventKey], ...patch }
    }));
  };

  const handleSave = async (eventKey) => {
    const draft = drafts[eventKey];
    if (!draft?.body_text?.trim()) {
      toast.error('Body text is required');
      return;
    }
    const missing = missingRequiredPlaceholders(
      draft.body_text,
      events.find((e) => e.event_key === eventKey)?.placeholders
    );
    if (missing.length) {
      toast.error(`Add placeholders: ${missing.map((m) => m.token).join(', ')}`);
      return;
    }
    setBusyKey(eventKey);
    saveMutation.mutate({
      eventKey,
      payload: {
        body_text: draft.body_text,
        button_text: draft.button_text
      }
    });
  };

  const handleSubmit = async (eventKey) => {
    const draft = drafts[eventKey];
    if (!draft?.body_text?.trim()) {
      toast.error('Body text is required');
      return;
    }
    if (!whatsappConfigured) {
      toast.error('Configure WhatsApp integration first');
      return;
    }
    const server = events.find((e) => e.event_key === eventKey);
    const dirty =
      draft.body_text !== (server?.body_text || '') ||
      draft.button_text !== (server?.button_text || '');
    setBusyKey(eventKey);
    try {
      if (dirty) {
        await updateVendorWhatsappTemplate(eventKey, {
          body_text: draft.body_text,
          button_text: draft.button_text
        });
      }
      submitMutation.mutate(eventKey);
    } catch (err) {
      setBusyKey(null);
      toast.error(err?.response?.data?.message || err?.message || 'Could not save before submit');
    }
  };

  const handleToggleEnabled = (eventKey, enabled) => {
    onChangeDraft(eventKey, { enabled });
    setBusyKey(eventKey);
    enabledMutation.mutate({ eventKey, enabled });
  };

  if (!isOpen) return null;

  return (
    <Modal
      width={56}
      name="Vendor WhatsApp templates"
      confirmOnClose
      isDirty={isDirty}
      onClose={onClose}>
      <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-600">
            Templates are required for vendor RFQ / quote / PO WhatsApp. Without Meta approval,
            email is still sent.
          </p>
          <button
            type="button"
            disabled={!whatsappConfigured || syncMutation.isPending || isFetching}
            onClick={() => syncMutation.mutate()}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
            {syncMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            Sync status
          </button>
        </div>

        {!whatsappConfigured ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
            WhatsApp is not configured for this builder.{' '}
            <Link
              to="/builder/integration"
              className="font-semibold underline"
              onClick={onClose}>
              Configure in Integration
            </Link>
          </div>
        ) : null}

        {isLoading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading templates…
          </div>
        ) : isError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
            <p>Could not load templates.</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-2 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50">
              Try again
            </button>
          </div>
        ) : (
          events.map((event) => (
            <EventTemplateCard
              key={event.event_key}
              event={event}
              draft={
                drafts[event.event_key] || {
                  body_text: event.body_text || '',
                  button_text: event.button_text || '',
                  enabled: !!event.enabled
                }
              }
              onChangeDraft={onChangeDraft}
              onSave={handleSave}
              onSubmit={handleSubmit}
              onToggleEnabled={handleToggleEnabled}
              busyKey={busyKey}
              textareaRefs={textareaRefs}
            />
          ))
        )}
      </div>
    </Modal>
  );
};

export default VendorWhatsAppTemplatesModal;
